import { useEffect, useState } from 'react'
import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cn } from 'app/lib/utils'

type ToggleProps = {
  label?: string
  defaultValue?: boolean
  handleToggle?: (val: boolean) => Promise<void | boolean> | void | boolean
  showLabelOnLeft?: boolean
  labelClassName?: string
  size?: 'sm' | 'md' | 'lg'
  name?: string
  controlled?: boolean
}

const sizeClasses = {
  sm: 'h-4 w-7',
  md: 'h-5 w-9',
  lg: 'h-7 w-12',
}

const thumbSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

const thumbTranslateClasses = {
  sm: 'data-[state=checked]:translate-x-3',
  md: 'data-[state=checked]:translate-x-4',
  lg: 'data-[state=checked]:translate-x-[22px]',
}

export function Toggle({
  label,
  defaultValue = false,
  handleToggle,
  showLabelOnLeft = false,
  labelClassName,
  size = 'md',
  name,
  controlled,
}: ToggleProps) {
  const [enabled, setEnabled] = useState(defaultValue)
  useEffect(() => setEnabled(defaultValue), [defaultValue])

  const onToggle = async (val: boolean) => {
    if (controlled) {
      const result = await handleToggle?.(val)
      if (result === true) {
        setEnabled(val)
      }
    } else {
      setEnabled(val)
      await handleToggle?.(val)
    }
  }

  return (
    <div className="flex items-center">
      {label && showLabelOnLeft && (
        <label
          className={cn('!mb-0 -order-1 mx-2 text-right text-ui-xs text-default cursor-pointer', labelClassName)}
          onClick={() => onToggle(!enabled)}
        >
          {label}
        </label>
      )}
      <SwitchPrimitives.Root
        name={name}
        checked={enabled}
        disabled={!handleToggle}
        onCheckedChange={onToggle}
        className={cn(
          'relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
          'transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:opacity-50',
          'data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-surface-strong',
          sizeClasses[size],
        )}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none block rounded-full bg-(--switch-thumb,var(--surface)) shadow ring-0',
            'transition-transform duration-200 ease-in-out',
            'data-[state=unchecked]:translate-x-0',
            thumbSizeClasses[size],
            thumbTranslateClasses[size],
          )}
        />
      </SwitchPrimitives.Root>
      {label && !showLabelOnLeft && (
        <label
          className={cn('!mb-0 ml-2 text-ui-xs text-default cursor-pointer', labelClassName)}
          onClick={() => onToggle(!enabled)}
        >
          {label}
        </label>
      )}
    </div>
  )
}
